"use client";

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import Search from './Search';
import { Menu, X, Search as SearchIcon } from 'lucide-react';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetHeader,
  SheetTitle 
} from "@/components/ui/sheet";

const Navbar = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Pricing', path: '/#pricing', id: 'pricing' },
    { name: 'Blog', path: '/blog' },
    { name: 'Docs', path: '/docs' },
    { name: 'Roadmap', path: '/roadmap' },
    { name: 'Support', path: '/support' },
  ];

  const scrollToSection = (id: string) => {
    if (isHome) {
      const element = document.getElementById(id);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] px-6 py-4 md:px-12 md:py-6 flex items-center justify-between transition-all duration-500 ${
        isHome ? 'bg-transparent' : 'bg-slate-950/80 backdrop-blur-xl border-b border-white/5'
      }`}>
        <Link to="/" className="flex items-center gap-3 group relative z-10">
          <Logo size="sm" />
          <span className="text-lg font-bold text-white tracking-tight">Hanza Sync</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <Link 
              key={item.name}
              to={item.path}
              onClick={() => item.id && scrollToSection(item.id)}
              className={`text-sm font-medium transition-colors hover:text-fuchsia-400 ${
                (location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)))
                  ? 'text-fuchsia-500' 
                  : 'text-slate-400'
              }`}
            >
              {item.name}
            </Link>
          ))}
          
          <button 
            onClick={() => setIsSearchOpen(true)}
            className="p-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Search"
          >
            <SearchIcon className="w-5 h-5" />
          </button>

          <Link to="/#contact" onClick={() => scrollToSection('contact')}>
            <button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-5 py-2 rounded-full text-sm font-bold transition-all active:scale-95 shadow-lg shadow-fuchsia-500/20">
              Get Started
            </button>
          </Link>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden flex items-center gap-2">
          <button 
            onClick={() => setIsSearchOpen(true)}
            className="p-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Search"
          >
            <SearchIcon className="w-5 h-5" />
          </button>

          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button 
                className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-slate-950 border-white/10 p-0 w-[300px]">
              <SheetHeader className="p-6 border-b border-white/5">
                <SheetTitle className="text-left flex items-center gap-3">
                  <Logo size="sm" />
                  <span className="text-white font-bold">Hanza Sync</span>
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col p-6 gap-4">
                {navItems.map((item) => (
                  <Link 
                    key={item.name}
                    to={item.path}
                    onClick={() => {
                      if (item.id) scrollToSection(item.id);
                      else setIsOpen(false);
                    }}
                    className={`text-lg font-medium py-2 transition-colors ${
                      (location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)))
                        ? 'text-fuchsia-500' 
                        : 'text-slate-400'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link 
                  to="/#contact" 
                  className="mt-4"
                  onClick={() => scrollToSection('contact')}
                >
                  <button className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white py-4 rounded-2xl text-lg font-bold shadow-lg shadow-fuchsia-500/20">
                    Get Started
                  </button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <Search isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Navbar;